import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import type { ReactNode } from 'react';

import {
  DISPUTE_STATUS_COLOR,
  getDisputeStatusLabel,
  isDisputeStatus,
} from '~/components/disputes/types';
import { InvoiceIdentityFields } from '~/components/invoices/detailPage/InvoiceIdentityFields';
import { InvoiceSummaryFields } from '~/components/invoices/detailPage/InvoiceSummaryFields';
import {
  AdjustedInvoiceBanner,
  AdjustmentChainSection,
  SourceSection,
} from '~/components/invoices/InvoiceOverview';
import type { Invoice } from '~/data/schemas/invoice';
import {
  invoiceKindToLabelMap,
  invoiceStatusToColorMap,
  invoiceStatusToLabelMap,
  isCredit,
} from '~/data/schemas/invoice';
import { BUTTON_SX, pageBandColor, pageBandDividerColor, SURFACE_RADIUS } from '~/theme';
import { useFeatureFlags } from '~/utils/featureFlags';
import { toDateFormatMMddyyyy } from '~/utils/format';

interface InvoiceDetailPageHeaderProps {
  invoice: Invoice | undefined;
  onBack: (() => void) | null;
  backLabel?: string;
  onOpenDocument: () => void;
  hasDocument: boolean;
  /** `null` for a reader who can't act on spending, which is what removes the button. */
  onEdit: (() => void) | null;
  editLabel: string;
  aiReview: ReactNode;
  /** `null` on phones, where the decision buttons move to the bar pinned at the bottom. */
  actions: ReactNode;
  actionsWidth?: number;
  compact?: boolean;
  decision?: ReactNode;
  footer?: ReactNode;
}

export function InvoiceDetailPageHeader({
  invoice,
  onBack,
  backLabel,
  onOpenDocument,
  hasDocument,
  onEdit,
  editLabel,
  aiReview,
  actions,
  actionsWidth = 170,
  compact = false,
  decision,
  footer,
}: InvoiceDetailPageHeaderProps) {
  const featureFlags = useFeatureFlags();
  const showDisputeManagement = featureFlags.disputeManagement === true;
  // `disputeStatus` is an open string on the schema, so narrow it against the map the
  // chip reads from: an unrecognised status renders no chip rather than "Dispute undefined".
  const disputeStatus = isDisputeStatus(invoice?.disputeStatus) ? invoice.disputeStatus : undefined;

  const statusLine = (
    <Box display="flex" flexDirection="row" alignItems="baseline" gap={1}>
      {invoice ? (
        <>
          <Typography variant="body2">{toDateFormatMMddyyyy(invoice.date)}</Typography>
          {isCredit(invoice) ? null : (
            <Chip
              label={invoiceStatusToLabelMap.get(invoice.status) ?? 'unknown'}
              size="small"
              variant="outlined"
              color={invoiceStatusToColorMap.get(invoice.status) ?? 'primary'}
            />
          )}
          {showDisputeManagement && disputeStatus && disputeStatus !== 'none' ? (
            <Chip
              label={`Dispute ${getDisputeStatusLabel(disputeStatus, invoice.disputeNeedsAttention)}`}
              size="small"
              color={DISPUTE_STATUS_COLOR[disputeStatus]}
            />
          ) : null}
        </>
      ) : (
        <Skeleton width={125} animation="wave" />
      )}
    </Box>
  );

  const titleLines = (
    <>
      <Typography variant="h5" noWrap>
        {invoice ? (
          `${invoiceKindToLabelMap.get(invoice.kind)} #${invoice.number}`
        ) : (
          <Skeleton width={200} sx={{ fontSize: '1.5rem' }} animation="wave" />
        )}
      </Typography>
      <Box display="flex" alignItems="center" gap={0.5} ml={-0.5}>
        <Button
          size="small"
          startIcon={<DescriptionOutlinedIcon />}
          onClick={onOpenDocument}
          disabled={!hasDocument}
          sx={BUTTON_SX}
        >
          {hasDocument ? 'Show document' : 'No document'}
        </Button>
        {onEdit ? (
          <Button
            size="small"
            startIcon={<EditOutlinedIcon />}
            onClick={onEdit}
            disabled={!invoice}
            sx={BUTTON_SX}
          >
            {editLabel}
          </Button>
        ) : null}
      </Box>
    </>
  );

  const identity = (
    <InvoiceIdentityFields invoice={invoice}>
      {invoice ? <SourceSection invoice={invoice} variant="field" /> : null}
      {showDisputeManagement && invoice && !invoice.parentInvoice && !isCredit(invoice) ? (
        <AdjustmentChainSection
          invoice={invoice}
          adjustments={invoice.adjustments ?? []}
          variant="field"
        />
      ) : null}
    </InvoiceIdentityFields>
  );

  if (compact) {
    return (
      <Box px={2} py={2} sx={{ bgcolor: pageBandColor }}>
        {showDisputeManagement && invoice ? <AdjustedInvoiceBanner invoice={invoice} /> : null}
        {decision ? <Box mb={2}>{decision}</Box> : null}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          columnGap={2}
          rowGap={1}
        >
          {statusLine}
          {decision ? null : actions}
        </Box>
        <Stack gap="3px" alignItems="flex-start" mt="3px">
          {titleLines}
        </Stack>
        <Box mt={2}>{aiReview}</Box>
        <Box mt={2}>
          <InvoiceIdentityFields invoice={invoice} showConsultant={false} />
        </Box>
        <Divider sx={{ my: 2, borderColor: pageBandDividerColor }} />
        <InvoiceSummaryFields invoice={invoice} stacked />
        {footer ? <Box mt={2}>{footer}</Box> : null}
      </Box>
    );
  }

  return (
    <Box
      px={{ xs: 2, md: 3 }}
      pt={{ xs: 2, md: 3 }}
      pb={{ xs: 2, md: 3 }}
      sx={{
        bgcolor: pageBandColor,
        borderBottom: 1,
        borderColor: 'divider',
        borderTopLeftRadius: SURFACE_RADIUS,
        borderTopRightRadius: SURFACE_RADIUS,
      }}
    >
      <Box display="flex" alignItems="flex-start" gap={3}>
        {onBack ? (
          <Box flexShrink={0} ml={-1}>
            <Tooltip title={backLabel ?? ''}>
              <IconButton onClick={onBack} aria-label={backLabel ?? 'Back'} size="small">
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ) : null}

        <Box flex={1} minWidth={0}>
          {showDisputeManagement && invoice ? <AdjustedInvoiceBanner invoice={invoice} /> : null}

          <Box display="flex" alignItems="flex-start" gap={2} flexWrap="wrap">
            <Stack gap="3px" minWidth={0} alignItems="flex-start" flexShrink={0}>
              {statusLine}
              {titleLines}
              <Divider flexItem sx={{ mt: 1, borderColor: pageBandDividerColor }} />
            </Stack>

            <Box
              minWidth={0}
              width={{ xs: '100%', lg: 'auto' }}
              sx={{ flex: { lg: '1 1 560px' }, maxWidth: { lg: 820 } }}
            >
              {aiReview}
            </Box>
          </Box>

          <Box mt={{ xs: 2, md: 4 }}>{identity}</Box>

          <Divider sx={{ my: 2.5, borderColor: pageBandDividerColor }} />
          <InvoiceSummaryFields invoice={invoice} />
        </Box>

        {actions ? (
          <Box flexShrink={0} width={actionsWidth}>
            {actions}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}
